import React from 'react';
import {FlatList} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import withIsConnected from '../../hoc/withIsConnected';
import BackSubheader from '../../elements/BackSubheader';
import ScreenContext from '../../ScreenContext';
import {View, StyleSheet} from 'react-native';
import AVText from '../../elements/AVText';
import Styles from '../../Styles';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import type {BlockedUser} from 'src/types/Snack';
import HttpClient from 'src/api/HttpClient';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import Events from 'src/logging/Events';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';

type SnackBlockedUsersProps = Record<string, never>;
type SnackBlockedUsersState = {
  loadingBlockedUsers: boolean;
  blockedUsers: Array<BlockedUser>;
};

class SnackBlockedUsersScreen extends React.Component<
  SnackBlockedUsersProps,
  SnackBlockedUsersState
> {
  static contextType = ScreenContext;

  constructor(props) {
    super(props);
    this.state = {
      loadingBlockedUsers: true,
      blockedUsers: [],
    };
    this.renderItem = this.renderItem.bind(this);
    this.getKeyForListItem = this.getKeyForListItem.bind(this);
    this.getSnackBlockedUsers = this.getSnackBlockedUsers.bind(this);
    this.unblockSnackUser = this.unblockSnackUser.bind(this);
    this.renderEmpty = this.renderEmpty.bind(this);
    this.renderBlockedUsers = this.renderBlockedUsers.bind(this);
    this.renderNoUsersBlockedView = this.renderNoUsersBlockedView.bind(this);
  }

  componentDidMount() {
    this.getSnackBlockedUsers();
  }

  async getSnackBlockedUsers() {
    this.context.actions.showSpinner();

    try {
      const response = await ActionsFactory.getAccountActions().getSnackBlockedUsers(
        AccountStore.getAccountId(),
      );
      this.setState({
        blockedUsers: response,
      });
    } catch (err) {
      const errorCode = HttpClient.parseJsonSafe(
        err?.statusCode ?? err.statusCode,
      );

      if (errorCode === 404) {
        alertError(Localized.Errors.error_fetching_blocked_users);
      } else {
        Events.Error.trackEvent(
          'Exception',
          'SnackBlockedUsersScreen:getSnackBlockedUsers',
          err.message ? err.message : err.toString(),
        );
      }
    } finally {
      this.setState({
        loadingBlockedUsers: false,
      });
      this.context.actions.hideSpinner();
    }
  }

  async unblockSnackUser(blockedAccountId: string) {
    this.context.actions.showSpinner();

    try {
      await ActionsFactory.getAccountActions().unblockSnackUser(
        AccountStore.getAccountId(),
        blockedAccountId,
      );
      this.getSnackBlockedUsers();
    } catch (err) {
      const errorCode = HttpClient.parseJsonSafe(
        err?.statusCode ?? err.statusCode,
      );

      if (errorCode === 404) {
        alertError(Localized.Errors.unable_to_save_changes);
      } else {
        Events.Error.trackEvent(
          'Exception',
          'SnackBlockedUsersScreen:unblockSnackUser',
          err.message ? err.message : err.toString(),
        );
      }
    } finally {
      this.context.actions.hideSpinner();
    }
  }

  getKeyForListItem(item) {
    return item.blockedAccountId;
  }

  renderItem({item}) {
    return (
      <View style={styles.itemContainer}>
        <AVText style={styles.textStyle}>{item.blockedName}</AVText>
        <RoundedButton
          buttonType={ButtonType.outline}
          text={Localized.Buttons.unblock}
          color={Styles.auxColor}
          onPress={() => {
            this.unblockSnackUser(item.blockedAccountId);
          }}
        />
      </View>
    );
  }

  renderNoUsersBlockedView() {
    return (
      <AVText style={styles.noUsersBlockedMessageTextStyle}>
        {Localized.Labels.no_users_blocked_snack_msg}
      </AVText>
    );
  }

  renderEmpty() {
    return <BackSubheader title={Localized.Labels.blocked_users} />;
  }

  renderBlockedUsers() {
    return (
      <FlatList
        refreshing={this.state.loadingBlockedUsers}
        onRefresh={this.getSnackBlockedUsers}
        data={this.state.blockedUsers}
        renderItem={this.renderItem}
      />
    );
  }

  render() {
    if (this.state.loadingBlockedUsers) {
      return this.renderEmpty();
    } else {
      return (
        <BackSubheader title={Localized.Labels.blocked_users}>
          {this.state.blockedUsers.length > 0
            ? this.renderBlockedUsers()
            : this.renderNoUsersBlockedView()}
        </BackSubheader>
      );
    }
  }
}

const styles = StyleSheet.create({
  itemContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: Styles.Spacing.m3,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Styles.gray,
  },
  textStyle: {
    fontSize: Styles.Fonts.f1,
  },
  noUsersBlockedMessageTextStyle: {
    fontSize: Styles.Fonts.f1,
    marginTop: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m2,
    textAlign: 'center',
  },
  listContainer: {
    flex: 1,
  },
});
export default withForwardedNavigationParams()(
  withIsConnected(SnackBlockedUsersScreen),
);
